import { useLocalStorage } from '@vueuse/core';
import type { ComputedRef, Ref } from 'vue';
import { computed } from 'vue';

import { usePartitionedStorageKey } from '@rygen/rygen-ui/composables';
import { Optional } from '@rygen/rygen-ui/model';

import { DashboardSummary, DashboardVersion } from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';

export interface UseDashboardVersionResponse {
  isVnextAvailable: ComputedRef<boolean>;
  dashboardVersion: Ref<DashboardVersion>;
  embedUrl: ComputedRef<string | undefined>;
}

export const useDashboardVersion = (
  dashboard: Ref<Optional<DashboardSummary>>,
): UseDashboardVersionResponse => {
  const { currentUser } = useCurrentUserStore();

  const { storageKey } = usePartitionedStorageKey('dashboard-vnext', {
    currentUser,
  });

  const preferenceKey = computed(
    () => `${storageKey.value}-${dashboard.value?.naturalId ?? 'no-dashboard'}`,
  );

  const preference = useLocalStorage<Optional<DashboardVersion>>(
    preferenceKey,
    undefined,
    { writeDefaults: false },
  );

  const isVnextAvailable = computed(() => !!dashboard.value?.vnextEmbedUrl);

  const dashboardVersion = computed<DashboardVersion>({
    get() {
      if (!isVnextAvailable.value) {
        return DashboardVersion.CURRENT;
      }

      if (preference.value) {
        return preference.value;
      }

      return dashboard.value?.vnextDefault
        ? DashboardVersion.VNEXT
        : DashboardVersion.CURRENT;
    },
    set(version: DashboardVersion) {
      preference.value = version;
    },
  });

  const embedUrl = computed(() => {
    const isVnext = dashboardVersion.value === DashboardVersion.VNEXT;
    return isVnext ? dashboard.value?.vnextEmbedUrl : dashboard.value?.embedUrl;
  });

  return {
    isVnextAvailable,
    dashboardVersion,
    embedUrl,
  };
};
